import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Lightning, Upload, Cpu, ListChecks, Sparkle, ArrowsClockwise,
  Broadcast, SignOut, Target, EnvelopeSimple, UserCircle, Trash, List, ChatCircleDots, Lock,
} from "@phosphor-icons/react";
import { api, http } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Refunds from "@/pages/Refunds";
import ResponsibleGambling from "@/pages/ResponsibleGambling";
import VerifyEmail from "@/pages/VerifyEmail";
import Admin from "@/pages/Admin";
import CookieBanner from "@/components/CookieBanner";
import AccountSettings from "@/components/AccountSettings";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import Login from "@/pages/Login";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import Uploads from "@/pages/Uploads";
import Optimizer from "@/pages/Optimizer";
import RawOptimizer from "@/pages/RawOptimizer";
import Lineups from "@/pages/Lineups";
import DataPipeline from "@/pages/DataPipeline";
import WarRoom from "@/pages/WarRoom";
import LateSwap from "@/pages/LateSwap";
import Hub from "@/pages/Hub";
import MobileSwap from "@/pages/MobileSwap";
import SlateLocker from "@/pages/SlateLocker";
import HubDrawer from "@/components/HubDrawer";
import Onboarding from "@/components/Onboarding";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import KickoffCountdown from "@/components/KickoffCountdown";
import HealthPingBadge from "@/components/HealthPingBadge";
import InjuryPushWatcher from "@/components/InjuryPushWatcher";
import GameTicker from "@/components/GameTicker";
import ErrorBoundary from "@/components/ErrorBoundary";
import ThemeToggle from "@/components/ThemeToggle";
import SlateBleedGuard, { announceSlateApply } from "@/components/SlateBleedGuard";

import "@/App.css";

// Sprint 62.2 — header trim: four primary destinations (Slate → Build →
// Portfolio, plus Manual as the power-user door). Intel / Simulation /
// Late Swap live under "More" on desktop; mobile keeps the flat strip.
const TABS = [
  { key: "uploads", label: "Slate", icon: Upload, tid: TID.navUploads },
  { key: "optimizer", label: "Build", icon: Cpu, tid: TID.navOptimizer },
  { key: "lineups", label: "Portfolio", icon: List, tid: "nav-my-lineups" },
  { key: "raw_optimizer", label: "Manual", icon: Sparkle, tid: "nav-raw-optimizer" },
  { key: "data", label: "Slate Intel", icon: Broadcast, tid: TID.navData, more: true },
  { key: "warroom", label: "Simulation", icon: Target, tid: TID.navWarRoom, more: true },
  { key: "late_swap", label: "Late Swap", icon: ArrowsClockwise, tid: "nav-late-swap", more: true },
  { key: "locker", label: "Slate Locker", icon: Lock, tid: "nav-locker", more: true },
  { key: "hub", label: "Hub", icon: ChatCircleDots, tid: "nav-hub", more: true },
];
const PRIMARY_TABS = TABS.filter((t) => !t.more);
const MORE_TABS = TABS.filter((t) => t.more);

function ProtectedRoute({ children, requireSub = false, requireAdmin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="h-1 w-64 mm-scan-bar" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requireSub && user.subscription_status !== "active") return <Navigate to="/pricing" replace />;
  // Sprint 60.98 — Admin route gate. Non-admin users get redirected to
  // /app entirely, so they can't even see the 403 wall (and won't be
  // tipped off that an admin page exists at that URL).
  if (requireAdmin && !user.is_admin) return <ClaimAdmin />;
  return children;
}

// Founder self-service admin promotion — enter the deploy's ADMIN_RESET_SECRET.
function ClaimAdmin() {
  const { user, checkAuth } = useAuth();
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!secret.trim()) return;
    setBusy(true);
    try {
      await http.post("/auth/claim-admin", { admin_secret: secret.trim() });
      toast.success("Admin access granted");
      await checkAuth?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid admin secret");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6" data-testid="claim-admin-page">
      <form onSubmit={submit} className="w-full max-w-md bg-[#121212] border border-zinc-800 p-6 space-y-4">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Restricted</div>
        <div className="heading text-2xl font-black uppercase">Admin access</div>
        <div className="text-sm text-zinc-400">
          Signed in as <span className="text-white">{user?.email}</span>. This account isn't an admin.
          Enter the admin secret from your deployment settings to claim access.
        </div>
        <input
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder="Admin secret"
          data-testid="claim-admin-secret-input"
          className="w-full bg-black border border-zinc-800 px-3 py-2 mono text-sm focus:border-[#39FF14] outline-none"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy || !secret.trim()}
            data-testid="claim-admin-submit"
            className="px-4 py-2 bg-[#39FF14] text-black font-black uppercase tracking-widest text-[11px] disabled:opacity-50"
          >
            {busy ? "Checking…" : "Claim admin"}
          </button>
          <Link to="/app" className="mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white">Back to app</Link>
        </div>
      </form>
    </div>
  );
}

function AppShell() {
  const { user, logout } = useAuth();
  // Sprint 60.140 — Hydrate initial tab from URL hash so browser
  // back/forward walks through the tabs the user visited instead of
  // ejecting them out of /app entirely (previously users hit back and
  // got dumped on /login, which felt like a forced logout). We also
  // push a hash on every tab change below so the history stack stays
  // in sync with what the user sees.
  const _initialTab = (() => {
    try {
      const h = (window.location.hash || "").replace(/^#/, "");
      const known = ["uploads", "data", "optimizer", "raw_optimizer", "lineups", "warroom", "late_swap", "locker"];
      return known.includes(h) ? h : "uploads";
    } catch { return "uploads"; }
  })();
  const [tab, _setTab] = useState(_initialTab);
  const [moreOpen, setMoreOpen] = useState(false);
  const setTab = (next) => {
    _setTab(next);
    try {
      const newHash = `#${next}`;
      if (window.location.hash !== newHash) {
        window.history.pushState({ tab: next }, "", newHash);
      }
    } catch { /* history API unavailable */ }
  };
  const [summary, setSummary] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const nav = useNavigate();

  // Sprint 60.140 — Sync tab with the browser history stack. When the
  // user clicks Back after navigating between tabs, the popstate event
  // fires and we restore the previous tab in-place instead of the
  // browser leaving /app entirely (which was logging users out
  // effectively because they landed on /login or the marketing page).
  useEffect(() => {
    const onPop = () => {
      try {
        const h = (window.location.hash || "").replace(/^#/, "");
        const known = ["uploads", "data", "optimizer", "raw_optimizer", "lineups", "warroom", "late_swap", "locker"];
        if (known.includes(h)) _setTab(h);
      } catch { /* noop */ }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const refresh = async () => {
    try {
      const s = await api.summary();
      setSummary(s);
    } catch {}
  };

  // First-run auto-detect: if user has zero DK players uploaded on first landing,
  // auto-detect and apply the primary DK slate so they land in Optimizer ready to go.
  useEffect(() => {
    (async () => {
      try {
        const s = await api.summary();
        setSummary(s);
        const alreadyLoaded = (s?.dk_pool_size || s?.dk?.n_players || 0) > 0;
        const seenAutoDetect = localStorage.getItem("wp_seen_autodetect");
        if (!alreadyLoaded && !seenAutoDetect) {
          try {
            const r = await api.dkSlateAutoDetect();
            if (r?.ok) {
              announceSlateApply(r);
              toast.success(`Auto-loaded ${r.player_count} players from DK · ${r.n_games}-game slate`);
              localStorage.setItem("wp_seen_autodetect", "1");
              refresh();
              // Sprint 60.117 — stay on Slate Selection after auto-detect
              // so first-run users see the onboarding tour in the correct
              // order (Slate Selection → Slate Intel → Optimizer → Lineups)
              // instead of getting yanked into the Optimizer while the
              // onboarding modal is still explaining Slate Selection.
            }
          } catch (e) {
            console.debug("auto-detect skipped:", e?.response?.data?.detail || e?.message);
            localStorage.setItem("wp_seen_autodetect", "1"); // don't nag on failure
          }
        }
      } catch { /* summary failed — leave user on default tab */ }
    })();
  }, []);

  // Refresh the top-nav summary whenever the pool changes upstream (mock
  // slate load, DK upload, auto-ingester tick). Keeps the DK player counter
  // and slate badge in sync without polling.
  useEffect(() => {
    const onPool = () => refresh();
    // Sprint 60.141 — Also refresh the LU counter on fill-gap
    // completion, no matter which page triggered it. Fixes the stale
    // "LU: 108" in the top-nav after a fill-gap that bumped the
    // portfolio to 150 on the My Lineups tab.
    const onFillGap = () => refresh();
    window.addEventListener("wp:pool-refreshed", onPool);
    window.addEventListener("wp:fill-gap-completed", onFillGap);
    return () => {
      window.removeEventListener("wp:pool-refreshed", onPool);
      window.removeEventListener("wp:fill-gap-completed", onFillGap);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-tab anchor scroll: components can dispatch wp:go-tab to jump the
  // shell to a tab and optionally flash a specific control by testid.
  useEffect(() => {
    const onGoTab = (e) => {
      const next = e?.detail?.tab;
      const anchor = e?.detail?.anchor;
      if (!next) return;
      setTab(next);
      if (anchor) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="${anchor}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("wp-anchor-flash");
            setTimeout(() => el.classList.remove("wp-anchor-flash"), 1600);
          }
        }, 120);
      }
    };
    window.addEventListener("wp:go-tab", onGoTab);
    // Alias: components have historically dispatched `wp:goto-tab` (extra
    // "to") — support both so buttons don't silently no-op depending on
    // which event name their file uses. Sprint 60.4: this was the actual
    // cause of the "GO TO OPTIMIZER & BUILD LINEUPS" button doing nothing.
    window.addEventListener("wp:goto-tab", onGoTab);
    return () => {
      window.removeEventListener("wp:go-tab", onGoTab);
      window.removeEventListener("wp:goto-tab", onGoTab);
    };
  }, []);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  // Sprint 60.136 — Split refresh vs. reset. Users expect the top-right
  // circular-arrow icon to REFRESH data (re-poll SGO/Sleeper/nflverse/
  // weather feeds, keep uploaded pool + lineups intact). The old behavior
  // wiped state, which was surprising and destructive. Reset is still
  // available but moved to a shift-click / hidden gesture.
  const [refreshing, setRefreshing] = useState(false);
  const onRefreshFeeds = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await api.dataRefresh();
      // Sprint 60.141 — Correct the "0 sources" cosmetic bug. Backend
      // returns `{sources_status: {vegas: "ok (12 games)", qbr: "ok (30)",
      // injuries: "ok (45)"}, counts: {...}}` — NOT an `updated` array.
      // Count sources whose status string starts with "ok" so the toast
      // reflects what actually refreshed instead of always saying 0.
      const status = r?.sources_status || {};
      const okSources = Object.entries(status).filter(([, v]) => String(v || "").startsWith("ok"));
      // Only real failures count as errors. "cached", "loading" and
      // "pending" are healthy in-between states (rankings load on the
      // scheduler's own cadence), and used to show a phantom "1 error".
      const isRealError = (v) => /^(error|timeout|unavailable|empty|failed)/i.test(String(v || ""));
      const errSources = Object.entries(status).filter(([, v]) => isRealError(v));
      const c = r?.counts || {};
      const summary = [
        c.vegas ? `Vegas ${c.vegas}` : null,
        c.injuries ? `Injuries ${c.injuries}` : null,
        c.qbr ? `QBR ${c.qbr}` : null,
      ].filter(Boolean).join(" · ");
      toast.success(
        `Feeds refreshed · ${okSources.length} source${okSources.length === 1 ? "" : "s"}${
          errSources.length ? ` · ${errSources.length} error${errSources.length === 1 ? "" : "s"}` : ""
        }`,
        {
          description: [summary, errSources.length ? errSources.map(([k]) => k.toUpperCase()).join(", ") + " failed" : null]
            .filter(Boolean).join(" · ") || undefined,
        },
      );
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Refresh failed";
      toast.error(String(msg));
    } finally {
      setRefreshing(false);
    }
  };
  const onReset = async () => {
    if (!window.confirm("This will WIPE your uploaded pool AND all generated lineups for the current slate.\n\nOnly use this if you're switching to a different slate. To just pull fresh injury/Vegas data, use the ↻ Refresh button.\n\nContinue?")) return;
    await api.reset();
    toast.success("State cleared");
    refresh();
  };

  return (
    <div className="min-h-screen w-full bg-[#0a0a0a] text-white">
      <EmailVerifyBanner user={user} />
      <header className="border-b border-zinc-800 bg-[#0a0a0a] sticky top-0 z-30">
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-6">
          <Link to="/app" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink-0">
            <img src="/brand/helmet_transparent.png" alt="WarPaint" className="w-8 h-8 sm:w-9 sm:h-9 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <div className="heading text-base sm:text-xl font-black uppercase tracking-tighter leading-none whitespace-nowrap">
                WarPaint DFS
              </div>
              <div className="overline mt-1 hidden min-[2100px]:block whitespace-nowrap">Rule-Based Game Theory Engine</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-shrink min-w-0">
            {PRIMARY_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key} data-testid={t.tid} onClick={() => setTab(t.key)}
                  title={t.label}
                  className={`px-3 py-2 text-[11px] uppercase tracking-widest transition-colors border whitespace-nowrap ${
                    active ? "bg-[#39FF14] text-black border-[#39FF14] font-semibold"
                           : "bg-transparent text-zinc-400 border-zinc-800 hover:bg-zinc-900 hover:text-white"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon size={14} weight={active ? "fill" : "regular"} />
                    <span>{t.label}</span>
                  </span>
                </button>
              );
            })}
            <div className="relative" data-testid="nav-more-wrap">
              <button
                type="button"
                data-testid="nav-more"
                onClick={() => setMoreOpen((v) => !v)}
                aria-expanded={moreOpen}
                className={`px-3 py-2 text-[11px] uppercase tracking-widest transition-colors border whitespace-nowrap inline-flex items-center gap-1.5 ${
                  MORE_TABS.some((t) => t.key === tab) ? "bg-[#39FF14] text-black border-[#39FF14] font-semibold"
                                                        : "bg-transparent text-zinc-400 border-zinc-800 hover:bg-zinc-900 hover:text-white"
                }`}
              >
                {MORE_TABS.find((t) => t.key === tab)?.label || "More"} ▾
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-[#0a0a0a] border border-zinc-800 min-w-[180px] shadow-xl" data-testid="nav-more-menu">
                  {MORE_TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                      <button
                        key={t.key} data-testid={t.tid}
                        onClick={() => { setTab(t.key); setMoreOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 ${
                          active ? "bg-[#39FF14]/10 text-[#39FF14]" : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                        }`}
                      >
                        <Icon size={14} weight={active ? "fill" : "regular"} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {summary && (
              <div className="hidden min-[2100px]:flex items-center gap-3 text-[10px] mono text-zinc-500 whitespace-nowrap">
                {summary.slate_type && summary.dk_pool_size > 0 && (
                  <SlateBadge slateType={summary.slate_type} nGames={summary.slate?.n_games} />
                )}
                <span>DK:<span className="text-white ml-1">{summary.dk_pool_size || 0}</span></span>
                <span>SRC:<span className="text-white ml-1">{summary.competitor_count}</span></span>
                <span>VEG:<span className="text-white ml-1">{summary.vegas_games}</span></span>
                <span>LU:<span className="text-[#39FF14] ml-1">{summary.last_lineups_count}</span></span>
                {summary.late_injury_outs?.length > 0 && (
                  <span className="text-[#FF3B30]">OUT:{summary.late_injury_outs.length}</span>
                )}
              </div>
            )}
            {/* Compact slate pill for laptop-sized viewports (lg-2xl) — keeps
                the user oriented (which slate is loaded) without pushing
                the header over-width. Full stats deck returns at 2xl+. */}
            {summary?.slate_type && summary?.dk_pool_size > 0 && (
              <div className="hidden lg:flex min-[2100px]:hidden">
                <SlateBadge slateType={summary.slate_type} nGames={summary.slate?.n_games} />
              </div>
            )}
            {/* Plan badge is compact — show earlier (xl) so users see their
                subscription state on laptops. Doubles as the account chip
                (clickable) when the full email doesn't fit. */}
            <button
              type="button"
              data-testid="btn-account-settings"
              onClick={() => setAccountOpen(true)}
              title={user?.email ? `Account · ${user.email}` : "Account settings"}
              className={`hidden xl:inline-flex items-center gap-2 text-[10px] mono px-2 py-0.5 border whitespace-nowrap transition-colors hover:bg-zinc-900 ${
                user?.subscription_status === "active" ? "border-[#39FF14] text-[#39FF14]" : "border-[#FFCC00] text-[#FFCC00]"
              }`}
            >
              <span className="hidden min-[2100px]:inline truncate max-w-[220px] text-zinc-400">{user?.email}</span>
              <span>{user?.plan?.toUpperCase() || "TRIAL"}</span>
            </button>
            <button
              data-testid="btn-refresh-feeds"
              onClick={onRefreshFeeds}
              disabled={refreshing}
              className={`p-2 border transition-colors ${
                refreshing
                  ? "text-[#39FF14] border-[#39FF14]/60 animate-pulse cursor-wait"
                  : "text-zinc-500 hover:text-[#39FF14] border-zinc-800 hover:border-[#39FF14]/50"
              }`}
              title={refreshing ? "Refreshing feeds…" : "Refresh feeds · Re-pull Vegas / injuries / weather / depth charts. Keeps your uploaded pool and lineups intact."}
            >
              <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              data-testid={TID.resetBtn}
              onClick={onReset}
              className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors border border-zinc-800 hover:border-[#FF3B30]/50"
              title="Reset slate · WIPES uploaded pool + all generated lineups. Only use when switching to a new slate."
            >
              <Trash size={14} />
            </button>
            {/* Compact account icon — visible below xl where the plan chip
                (which doubles as the account trigger) is hidden. Ensures
                every viewport has a path to Account Settings. */}
            <button
              type="button"
              data-testid="btn-account-settings-icon"
              onClick={() => setAccountOpen(true)}
              title={user?.email ? `Account · ${user.email}` : "Account settings"}
              className="xl:hidden p-2 text-zinc-500 hover:text-white transition-colors border border-zinc-800 hover:border-zinc-700"
            >
              <UserCircle size={14} />
            </button>
            <a
              data-testid="btn-contact-support"
              href="mailto:support@warpaintdfs.com?subject=WarPaint%20DFS%20Support"
              className="p-2 text-zinc-500 hover:text-[#39FF14] transition-colors border border-zinc-800 hover:border-[#39FF14]/50"
              title="Contact support"
            >
              <EnvelopeSimple size={14} />
            </a>
            {/* Sprint 60.98 — Admin console access. Visible only to users
                with `is_admin === true`; regular users never see this
                icon. Routes to /admin (protected server-side too). */}
            {user?.is_admin && (
              <a
                data-testid="btn-admin-console"
                href="/admin"
                className="p-2 text-[#39FF14] hover:text-white transition-colors border border-[#39FF14]/40 hover:border-[#39FF14] mono text-[10px] uppercase tracking-widest font-black px-2"
                title="Admin Console"
              >
                Admin
              </a>
            )}
            <HubDrawer />
            <ThemeToggle />
            <button
              data-testid="btn-logout" onClick={doLogout}
              className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors border border-zinc-800"
              title="Sign out"
            >
              <SignOut size={14} />
            </button>
          </div>
        </div>

        <div className="lg:hidden flex overflow-x-auto border-t border-zinc-800 scrollbar-none">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-3 text-[10px] uppercase tracking-widest whitespace-nowrap flex-shrink-0 ${
                  active ? "text-[#39FF14] border-b-2 border-[#39FF14]" : "text-zinc-500"
                }`}
              >{t.label}</button>
            );
          })}
        </div>

        <div className="border-t border-zinc-800 px-4 sm:px-6 py-2 overflow-x-auto scrollbar-none">
          <div className="flex items-center justify-end min-w-max">
            <HealthPingBadge />
            <KickoffCountdown />
          </div>
        </div>
      </header>

      <GameTicker />

      <main className="w-full px-6 py-6">
        {tab === "uploads" && <Uploads onChanged={refresh} summary={summary} />}
        {tab === "data" && <DataPipeline />}
        {tab === "optimizer" && <Optimizer onGenerated={refresh} />}
        {tab === "raw_optimizer" && <RawOptimizer />}
        {tab === "lineups" && <Lineups />}
        {tab === "locker" && <SlateLocker />}
        {tab === "warroom" && <WarRoom />}
        {tab === "late_swap" && <LateSwap />}
        {tab === "hub" && <Hub />}
      </main>

      <Onboarding />
      <OnboardingChecklist onGoTab={setTab} />
      <InjuryPushWatcher enabled={true} />
      {accountOpen && (
        <AccountSettings user={user} onClose={() => setAccountOpen(false)} />
      )}

      <SlateBleedGuard />
    </div>
  );
}

function SlateBadge({ slateType, nGames }) {
  const config = {
    MAIN: { color: "text-[#39FF14] border-[#39FF14]", label: "MAIN" },
    TURBO: { color: "text-[#FFCC00] border-[#FFCC00]", label: "TURBO" },
    MINI: { color: "text-[#FFCC00] border-[#FFCC00]", label: "MINI" },
    SHOWDOWN: { color: "text-[#FF3B30] border-[#FF3B30]", label: "SHOWDOWN" },
  }[slateType] || { color: "text-zinc-400 border-zinc-700", label: slateType };
  return (
    <span
      data-testid="slate-badge"
      className={`text-[10px] mono px-2 py-0.5 border ${config.color} font-bold tracking-widest whitespace-nowrap`}
      title={`${nGames ?? "?"} game slate`}
    >
      {config.label}{nGames ? ` · ${nGames}G` : ""}
    </span>
  );
}

function RouterRoot() {
  const location = useLocation();
  // Google OAuth callback: session_id in URL fragment — handle BEFORE routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingOrApp />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/responsible-gambling" element={<ResponsibleGambling />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/app" element={<ProtectedRoute requireSub><AppShell /></ProtectedRoute>} />
      <Route path="/swap" element={<ProtectedRoute requireSub><MobileSwap /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Redirect authenticated users away from the marketing landing page — they
// should always land inside /app. Unauthenticated visitors still see Landing.
// While auth is loading, render nothing (avoids Landing → /app flicker).
function LandingOrApp() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <RouterRoot />
            {/* Cap the stack: 5+ injury toasts used to cover bottom-right CTAs. */}
            <Toaster theme="dark" position="bottom-right" visibleToasts={3} duration={5000} />
            <CookieBanner />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
